"use client";

import { ThemeProvider, useTheme } from "next-themes";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { shouldForceDarkRoute } from "@/lib/constants";
import {
  applyRouteAppearance,
  isThemePreviewDocument,
  PREVIEW_MODE_STORAGE_KEY,
  readAppearanceMode,
} from "@/lib/theme-appearance-policy";

const PreviewThemeSync = dynamic(
  () =>
    import("@/components/theme-customizer/theme-live-sync").then(
      (module) => module.ThemeLiveSync,
    ),
  { ssr: false },
);

function AppearanceScopeController({ preview }: { preview: boolean }) {
  const { theme, setTheme } = useTheme();
  const initialized = useRef(false);
  useLayoutEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      applyRouteAppearance();
      // Client navigation into the kit migrates legacy preferences after the
      // provider initialized. Reconcile its state before paint in this scope.
      const preferred = readAppearanceMode(preview);
      if (theme !== preferred) setTheme(preferred);
    } else if (theme !== "light" && theme !== "dark" && theme !== "system") {
      setTheme("system");
    }
  }, [preview, theme, setTheme]);
  return null;
}

function PreviewSyncLoader({ enabled }: { enabled: boolean }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  // The server cannot inspect the embedding frame. Keep its tree identical
  // through hydration, then load preview-only synchronization when opted in.
  return mounted && enabled ? <PreviewThemeSync /> : null;
}

/**
 * The application's single theme controller.
 *
 * There must be exactly ONE next-themes ThemeProvider in the tree: next-themes
 * short-circuits any nested ThemeProvider to a passthrough (it renders its
 * children and applies nothing), so a second provider can never "override" the
 * first. The previous approach nested a forced-dark provider and then bolted a
 * MutationObserver onto <html class> to force dark anyway, which fought the root
 * provider's system-theme write in an unbounded class-mutation loop that froze
 * the page on any visitor whose resolved theme was not already dark.
 *
 * Instead, this one provider chooses its props by route:
 * - Marketing / landing / auth / legal: forced dark for every visitor,
 *   including UI-kit preview frames, so previews match production.
 * - Authenticated app + self-themed kits/showcases: honor the stored/system
 *   preference (and their own toggles).
 *
 * Normal route changes update forcedTheme without remounting the app tree.
 * Entering or leaving the UI-kit preview scope remounts this provider to switch
 * preference namespaces: next-themes initializes its state/storage callbacks
 * on mount. Preview mode changes must never persist into product preferences.
 */
export function RouteAwareThemeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const preview = isThemePreviewDocument(pathname ?? "");
  const forceDark = shouldForceDarkRoute(pathname);

  return (
    <ThemeProvider
      key={preview ? "preview" : "product"}
      attribute="class"
      storageKey={preview ? PREVIEW_MODE_STORAGE_KEY : "theme"}
      enableSystem
      enableColorScheme={false}
      defaultTheme="system"
      forcedTheme={forceDark ? "dark" : undefined}
      // The shared document bootstrap resolves iframe scope before paint.
      // next-themes cannot know that scope on the server, so its independent
      // bootstrap must not overwrite it. Its normal React mode controller stays.
      scriptProps={{ type: "text/plain" }}
    >
      <AppearanceScopeController preview={preview} />
      {children}
      <PreviewSyncLoader enabled={preview} />
    </ThemeProvider>
  );
}
